import { c as _c } from "react/compiler-runtime";
import { SphereSupportLink } from "@components/common/sphere-support-link";
import { ContactSupportLocations } from "@custom-types/types";
import { Banner } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useRouteMatch } from "@hooks/use-route-match";
import { Box } from "@mui-wrapper";
import { SettingsTabs } from "@router/route-params";
import { generateSettingRoute, useAppParams } from "@router/router-helper";
import { LIMIT_TYPE_WARNING_WHITELIST } from "@src/constants/subscription-constants";
import { selectQuotasSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { BannerIdentifier, dismissBanner, shouldShowBanner } from "@utils/banner-dismissal-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo, useState } from "react";

/** Banner to show when a subscription limit is exceeded */
export function ExceededSubscriptionLimitsBanner() {
  const $ = _c(10);
  const routeIdentifier = useRouteMatch();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    companyId
  } = useAppParams();
  const [isDismissed, setIsDismissed] = useState(false);
  const {
    navigateToSettingsPage
  } = useAppNavigation();
  const {
    canViewSubscriptionDetails
  } = useHasUserValidRoleCompanyLevel();
  const selectQuotas = useAppSelector(selectQuotasSelector);
  const subscriptionTabRoute = generateSettingRoute(companyId ?? "", SettingsTabs.subscription);
  const isAnyWhitelistedLimitExceeded = selectQuotas.some(_temp);
  const isNotDismissedRecently = shouldShowBanner(BannerIdentifier.LimitExceeded);
  const shouldDisplayBanner = routeIdentifier !== "subscriptionExpired" && isAnyWhitelistedLimitExceeded && !isDismissed && isNotDismissedRecently && canViewSubscriptionDetails;
  let t0;
  if ($[0] !== companyId || $[1] !== navigateToSettingsPage || $[2] !== trackEvent) {
    t0 = () => {
      trackEvent({
        name: WorkspaceEvents.viewSubscriptionPage
      });
      navigateToSettingsPage({
        settingsTab: SettingsTabs.subscription,
        companyId: companyId ?? ""
      });
    };
    $[0] = companyId;
    $[1] = navigateToSettingsPage;
    $[2] = trackEvent;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleSubscriptionNavigation = t0;
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      dismissBanner(BannerIdentifier.LimitExceeded);
      setIsDismissed(true);
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleBannerClose = t1;
  if (!shouldDisplayBanner || window.location.pathname === subscriptionTabRoute) {
    return null;
  }
  let t2;
  if ($[5] !== handleSubscriptionNavigation) {
    t2 = {
      label: "View More",
      action: handleSubscriptionNavigation
    };
    $[5] = handleSubscriptionNavigation;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>Your account currently exceeds the limits of your plan. Review which features you can reduce or contact{" "}<SphereSupportLink location={ContactSupportLocations.exceededSubscriptionLimitsBanner} />{" "}to upgrade your plan.</div>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2) {
    t4 = <Box data-testid="exceed-subscription-limit-banner"><Banner variant="warning" action={t2} onClose={handleBannerClose} title="Plan limit exceeded">{t3}</Banner></Box>;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp(quota) {
  return LIMIT_TYPE_WARNING_WHITELIST.includes(quota.identifier) && quota.current > quota.limit;
}